import {
  Button,
  Checkbox,
  message,
  Message,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  ModalOverlay,
  useDisclosure,
  VStack
} from '@payflip/ui/v2';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { LocaleContext } from '../locale-provider';
import { useOnboardEmployeeMutation } from './mutations.generated';
import { useGetOnboardingModalDataQuery } from './queries.cms.generated';
import {
  useGetTenantQuery,
  useGetUserFirstNameQuery
} from './queries.generated';

export function OnboardingModal(): JSX.Element | null {
  const [onboard, { loading }] = useOnboardEmployeeMutation();
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const { locale } = useContext(LocaleContext);
  const { data } = useGetOnboardingModalDataQuery({ locale });
  const { data: userFirstNameData } = useGetUserFirstNameQuery();
  const { register, handleSubmit, watch } = useForm<{
    termsAndConditions: boolean;
    privacyPolicy: boolean;
  }>();

  const { data: tenantData } = useGetTenantQuery();

  const termsAndConditionsField = watch('termsAndConditions');
  const privacyPolicy = watch('privacyPolicy');

  const cmsData = data?.employeeOnboardingModal?.data?.attributes ?? null;

  function onSubmit(): void {
    if (!termsAndConditionsField || !privacyPolicy) {
      return;
    }

    onboard();
    onClose();
  }

  if (typeof userFirstNameData === 'undefined') {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
    >
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>
          <ModalHeading>
            {message(cmsData?.title, 'Welcome to Payflip')}
          </ModalHeading>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Message
              text={cmsData?.description}
              fallback="Hi {{firstname}}, with Payflip, you can now personalize your salary package 💪<br /><br />Use Payflip to exchange your budget for benefits that you love ❤️"
              variables={{ firstname: userFirstNameData.user.firstname }}
            />

            <Checkbox {...register('termsAndConditions')} isRequired>
              <Message
                text={cmsData?.TermsAndConditionsCheckboxTextWithPlaceholder}
                fallback={`I agree with the <a href={{url}} target="_blank" rel="noopener noreferrer">terms and conditions</a> for the use of the Payflip tool`}
                variables={{
                  url: tenantData?.tenant?.termsAndConditionsLink ?? ''
                }}
              />
            </Checkbox>
            <Checkbox {...register('privacyPolicy')} isRequired>
              <Message
                text={cmsData?.privatePolicyCheckboxTextWithPlaceholder}
                fallback={`I agree with the <a href={{url}} target="_blank" rel="noopener noreferrer">privacy declaration</a> for the use of the Payflip tool`}
                variables={{
                  url: tenantData?.tenant?.privacyPolicyLink ?? ''
                }}
              />
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <Button
            type="submit"
            isDisabled={!termsAndConditionsField || !privacyPolicy || loading}
            isLoading={loading}
          >
            {message(cmsData?.continueButtonText, 'Continue to Payflip')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
